import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Chip,
} from '@mui/material'
import { keyframes } from '@mui/system'
import { visuallyHidden } from '@mui/utils'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import EastIcon from '@mui/icons-material/East'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, A11y, Keyboard } from 'swiper/modules'
import 'swiper/css'

import { getVLabList } from '@/api/vx'

export default function CourseIntroSection() {
  const { isMobile, isTablet } = useIsMobile()
  const lang = useLang()

  const [allItems, setAllItems] = useState([])

  const [isBeginning, setIsBeginning] = useState(true)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isEnd, setIsEnd] = useState(false)
  const [current, setCurrent] = useState(1)
  const swiperRef = useRef(null)

  // 시연용 강좌 썸네일 5개 고정 (파일서버 미완일 때 임시로 사용)
  const DEMO_THUMBS = [
    'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A004/A004_1.png',
    'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A003/A003_1.png',
    'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A005/A005_1.png',
    'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/B003/B003_1.png',
    'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/C001/C001_1.png',
    'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/C002/C002_1.png',

  ]

  const total = allItems.length

  useEffect(() => {
    let mounted = true

    const fetchLatest = async () => {
      try {
        const req = {
          page: 1,
          pageSize: 5,
          recordSize: 5,
          searchNm: '',
          grandDivCode: '',
          midiumDivCode: '',
          smallDivCode: '',
        }

        const res = await getVLabList(req)
        const list = res?.data?.content ?? []

        const items = list.map((row, index) => ({
          id: row.eduLectureId ?? index,
          title: row.eduTitle ?? '',

          institution: index % 2 === 0,
          lectureLevel: index % 2 === 0 ? '초급' : '중급',
          sessionTotal: row.eduCourseCnt ?? '0',

          // ===========================
          // 기존 로직(파일서버 연결 후 사용 예정)
          // thumbUrl: row.thumbUrl ?? '',
          //
          // 시연용 로직(오늘만 임시): picsum 5개 고정
          thumbUrl: DEMO_THUMBS[index] ?? '',
          // ===========================

          hashtag: ['#해시태그1', '#해시태그2'],

          Cat: '생활안전',
          subCat: '화재예방',
          detailCat: '기본',

          content: '고등, 평생, 직업교육기관의 학생들은 물론',
        }))

        if (mounted) {
          setAllItems(items)
          setCurrent(1)
          setIsBeginning(true)
          setIsEnd(items.length <= 1)
          if (swiperRef.current) {
            swiperRef.current.slideTo(0, 0)
          }
        }
      } catch (e) {
        if (mounted) {
          setAllItems([])
          setCurrent(1)
          setIsBeginning(true)
          setIsEnd(true)
        }
      }
    }

    fetchLatest()

    return () => {
      mounted = false
    }
  }, [])

  const handlePrev = () => {
    if (isTransitioning) return
    swiperRef.current?.slidePrev()
  }

  const handleNext = () => {
    if (isTransitioning) return
    swiperRef.current?.slideNext()
  }

  const arrow = keyframes` 
    0%, 60% {
      transform: translate(0, 0);
      opacity: 1;
    }
    20% {
      transform: translate(100%, 0);
      opacity: 1;
    }
    21% {
      opacity: 0;
    }
    50% {
      opacity: 0;
      transform: translate(-100%, 0);
    }
    51% {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  `

  return (
    <Box
      sx={{
        mx: 'auto',
        py: isMobile ? 10 : 15,
        width: '100%',
        minHeight: isMobile ? 'initial' : 'calc(100vh - 120px)',
        backgroundColor: 'grey.50',
      }}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'flex-end'}
        justifyContent={isMobile ? 'flex-start' : 'space-between'}
        spacing={2}
        sx={{ maxWidth: '1440px', px: 2, mx: 'auto', mb: 7 }}
      >
        <Box>
          <Typography
            component="h3"
            variant={isMobile ? 'body1' : 'subtitle3'}
            fontWeight={700}
            sx={{ color: 'primary.main', mb: 2 }}
          >
            VLab 강좌
          </Typography>
          <Typography
            component="p"
            fontWeight={600}
            variant={isMobile ? 'h6' : 'h4'}
          >
            OpenVLab의 <br />
            최신강좌를 확인해 보세요.
          </Typography>
        </Box>

        <Stack
          component={Link}
          to={buildLangHref('/contents/lecture', lang)}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pb: 1.5,
            textDecoration: 'none',
            color: 'inherit',
            display: 'inline-flex',
            '&:hover .arrow-icon': {
              animation: `${arrow} 0.5s ease-in-out`,
            },
          }}
        >
          <Typography fontWeight={600}>더 많은 강좌 보기</Typography>

          <IconButton
            aria-hidden="true"
            sx={{
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              border: '1px solid',
              borderColor: 'grey.dark',
              color: 'grey.dark',
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <EastIcon sx={{ fontSize: isMobile ? '16px' : '20px' }} />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        role="region"
        aria-label="VLab 최신 강좌 슬라이드"
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1440px',
          mx: 'auto',
          px: 2,
        }}
      >
        <style>{`
          .courseSwiper {
            overflow: visible !important;
          }
          .courseSwiper .swiper-wrapper {
            align-items: stretch;
          }
        `}</style>

        <Swiper
          modules={[Pagination, A11y, Keyboard]}
          spaceBetween={24}
          slidesPerView={isTablet ? 1.1 : 1.9}
          className="courseSwiper"
          allowTouchMove={isMobile}
          simulateTouch={isMobile}
          onSwiper={(sw) => {
            swiperRef.current = sw
            setCurrent(1)
            setIsBeginning(true)
            setIsEnd(sw.isEnd)
          }}
          onSlideChange={(sw) => {
            setIsBeginning(sw.isBeginning)
            setIsEnd(sw.isEnd)
          }}
          onSlideNextTransitionEnd={() => {
            setCurrent((prev) => Math.min(prev + 1, total || 1))
          }}
          onSlidePrevTransitionEnd={() => {
            setCurrent((prev) => Math.max(prev - 1, 1))
          }}
          onSlideChangeTransitionStart={() => {
            setIsTransitioning(true)
          }}
          onSlideChangeTransitionEnd={(sw) => {
            setTimeout(() => {
              setIsTransitioning(false)
              setIsBeginning(sw.isBeginning)
              setIsEnd(sw.isEnd)
            }, 80)
          }}
          onReachEnd={() => {
            setIsEnd(true)
          }}
          onFromEdge={() => {
            setIsEnd(false)
            setIsBeginning(false)
          }}
        >
          {allItems.map((item) => (
            <SwiperSlide
              key={item.id}
              style={{
                width: isTablet ? '100%' : 'calc(100% / 1.93)',
                borderRadius: 2,
                overflow: 'hidden',
                height: isTablet ? '100%' : '317px',
              }}
            >
              <Card
                component={Link}
                to={buildLangHref(`/contents/lectureview/${item.id}`, lang)}
                aria-label={`${item.title} 강좌 상세보기`}
                sx={{
                  width: '100%',
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  boxShadow: 'none',
                  height: '100%',
                }}
              >
                <CardMedia
                  component="img"
                  image={
                    item.thumbUrl && item.thumbUrl.trim() !== ''
                      ? item.thumbUrl
                      : '/assets/images/No image.png'
                  }
                  alt={`${item.title} 강좌 썸네일 이미지`}
                  sx={{
                    width: isMobile ? '100%' : '54%',
                    aspectRatio: 4 / 3,
                    objectFit: 'cover',
                  }}
                />

                <CardContent
                  sx={{
                    width: isMobile ? 'auto' : '46%',
                    p: isMobile ? 2 : 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* {item.institution && (
                      <Chip color="grey" variant="contained" label="기관" />
                    )} */}

                    <IconButton
                      aria-hidden="true"
                      sx={{
                        width: '32px',
                        height: '32px',
                        color: 'grey.dark',
                        bgcolor: 'grey.50',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        ml: 'auto',
                      }}
                    >
                      <EastIcon
                        className="arrow-icon"
                        sx={{ fontSize: '16px' }}
                      />
                    </IconButton>
                  </Stack>

                  <Typography
                    variant={isMobile ? 'subtitle2' : 'subtitle1'}
                    fontWeight={700}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    component="p"
                    variant="body2"
                    color="text.secondary"
                  >
                    {item.lectureLevel} | 강의 {item.sessionTotal}개
                  </Typography>

                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {item.content}
                  </Typography>
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>

        <Stack
          direction="row"
          justifyContent={isMobile ? 'center' : 'flex-start'}
          spacing={1.5}
          sx={{ mt: 4 }}
        >
          <IconButton
            className="course-button-prev"
            aria-label="이전 슬라이드"
            aria-disabled={isBeginning}
            disabled={isBeginning || isTransitioning}
            onClick={handlePrev}
            sx={{
              width: '48px',
              height: '48px',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              cursor: 'pointer',
            }}
          >
            <ArrowBackIosNewIcon
              sx={{
                fontSize: '20px',
                color: isBeginning ? 'grey.500' : 'text.primary',
              }}
            />
          </IconButton>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            sx={{ px: 2 }}
            aria-hidden="true"
          >
            <Typography fontWeight={700}>
              <span style={visuallyHidden}>현재</span>
              {isEnd ? total : current}
            </Typography>
            <Box sx={{ width: '1px', height: '12px', bgcolor: 'divider' }} />
            <Typography>
              <span style={visuallyHidden}>전체</span>
              {total}
            </Typography>
          </Stack>

          <IconButton
            className="course-button-next"
            aria-label="다음 슬라이드"
            aria-disabled={isEnd}
            disabled={isEnd || isTransitioning}
            onClick={handleNext}
            sx={{
              width: '48px',
              height: '48px',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              cursor: 'pointer',
            }}
          >
            <ArrowForwardIosIcon
              sx={{
                fontSize: '20px',
                color: isEnd ? 'grey.500' : 'text.primary',
              }}
            />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  )
}
